import { getBugsnagClient } from '@app/core/integrations/bugsnag/bugsnag';
import { MediaPlayer, PlayerEvent, TimeRange } from '@mi-front/core';
import debugFn from 'debug';
import { StateCreator, StoreApi } from 'zustand';
import { playbackRegistry } from '../registry/playback-registry';

const debug = debugFn('base-playback');

export type ExclusivePlaybackMode = 'pause' | 'muted';

export interface BasePlaybackState {
  mediaSrc: string | null;
  playbackRange?: TimeRange<number> | null;
  isPlaying: boolean;
  isMuted: boolean;
  isSeeking: boolean;
  currentTs: number | null;
  stopAtTs: number | null;
  player: MediaPlayer | null;
  exclusivePlaybackMode: ExclusivePlaybackMode;
  playbackId?: string;
}

export interface BasePlaybackActions {
  setupPlayer: (player: MediaPlayer | null) => void;
  setMedia: (media: { src: string | null; playbackRange?: TimeRange<number> | null }) => void;
  play: (args?: {
    startTs?: number;
    stopTs?: number;
    playAfterSeek?: boolean;
  }) => Promise<void>;
  pause: ({ calledFromPlayer }?: { calledFromPlayer?: boolean }) => void;
  onPlayerPlaying: () => void;
  dispose: () => void;
  mute: () => void;
  unmute: () => void;
}

export const basePlaybackInitialState: BasePlaybackState = {
  mediaSrc: null,
  playbackRange: null,
  isPlaying: false,
  isMuted: false,
  isSeeking: false,
  stopAtTs: null,
  currentTs: null,
  player: null,
  exclusivePlaybackMode: 'pause',
  playbackId: undefined,
};

/**
 * Converts timestamp offset to the format expected by the player.
 * Audio players expect milliseconds, video players expect seconds.
 */
export type SeekTimeConverter = (offsetMs: number) => number;

type Unsubscribe = () => void;

export const createBasePlaybackSlice = <
  TStore extends BasePlaybackState & BasePlaybackActions,
>(): StateCreator<
  BasePlaybackState & BasePlaybackActions,
  [],
  [],
  BasePlaybackState & BasePlaybackActions
> => {
  return (set, get, api) => {
    // Helper function to format debug messages with playbackId
    const debugLog = (message: string, ...args: any[]) => {
      const playbackId = get().playbackId;
      const prefix = playbackId ? `[${playbackId}] ` : '';
      debug(`${prefix}${message}`, ...args);
    };

    // Per-store-instance subscriptions (kept in this slice closure)
    const unsubs: Unsubscribe[] = [];
    const clearSubscriptions = () => {
      debugLog('clearing %d player event listeners', unsubs.length);

      while (unsubs.length) {
        unsubs.pop()?.();
      }
    };

    const getPlayer = () => {
      return get().player;
    };

    const convertTsToTime = (ts: number): number => {
      return (ts - (get().playbackRange?.from ?? 0)) / 1000;
    };

    const checkStop = (ts: number) => {
      const stopAtTs = get().stopAtTs;

      if (stopAtTs && ts >= stopAtTs) {
        debugLog('checkStop: stop condition met, pausing playback');
        set({ stopAtTs: null });
        get().pause();
      }
    };

    const subscribeToPlayerEvents = (player: MediaPlayer) => {
      debugLog('subscribeToPlayerEvents: subscribing to player events');

      const onTimeUpdate = (ts: number) => {
        set({ currentTs: ts });

        checkStop(ts);
      };

      const onError = (error: Error) => {
        getBugsnagClient()?.notify(error);
      };

      const onSeekingStart = () => {
        debugLog('seeking started');
        set({ isSeeking: true });
      };

      const onSeekingEnd = () => {
        debugLog('seeking ended');
        set({ isSeeking: false });
      };

      const onPlaying = () => {
        get().onPlayerPlaying();
      };

      const onPause = () => {
        get().pause({ calledFromPlayer: true });
      };

      player.on(PlayerEvent.TS_UPDATE, onTimeUpdate);
      player.on(PlayerEvent.ERROR, onError);
      player.on(PlayerEvent.SEEKING_START, onSeekingStart);
      player.on(PlayerEvent.SEEKING_END, onSeekingEnd);
      player.on(PlayerEvent.PLAYING, onPlaying);
      player.on(PlayerEvent.PAUSED, onPause);

      unsubs.push(() => player.off(PlayerEvent.TS_UPDATE, onTimeUpdate));
      unsubs.push(() => player.off(PlayerEvent.ERROR, onError));
      unsubs.push(() => player.off(PlayerEvent.SEEKING_START, onSeekingStart));
      unsubs.push(() => player.off(PlayerEvent.SEEKING_END, onSeekingEnd));
      unsubs.push(() => player.off(PlayerEvent.PLAYING, onPlaying));
      unsubs.push(() => player.off(PlayerEvent.PAUSED, onPause));
    };

    return {
      ...basePlaybackInitialState,
      setupPlayer: (player: MediaPlayer | null) => {
        if (!player) {
          debugLog('clear player in store');
          set({ player: null });
          return;
        }

        debugLog('set player in store');

        // Clear previous subscriptions before setting new player
        if (get().player === player) {
          console.warn('Player already set, ignoring');
          return;
        }

        set({
          player,
          isMuted: player.isMuted(),
        });

        subscribeToPlayerEvents(player);
      },

      setMedia: (media: { src: string | null; playbackRange?: TimeRange<number> | null }) => {
        set({
          mediaSrc: media.src,
          playbackRange: media.playbackRange ?? null,
        });
      },

      play: async (args?: {
        startTs?: number;
        stopTs?: number;
        playAfterSeek?: boolean;
      }) => {
        debugLog('play called: args=%o', args);

        // Apply exclusive playback mode to all other players before starting playback
        playbackRegistry.applyExclusivePlaybackMode(
          api as unknown as StoreApi<BasePlaybackState & BasePlaybackActions>,
        );

        if (!args) {
          debugLog('play with no args, just resume playback');
          getPlayer()?.play();
          return;
        }

        const player = getPlayer();

        // Mark seeking immediately so play/pause UI can show loading before the
        // player emits SEEKING_START (covers preload wait and preloaded seeks).
        set({
          currentTs: args.startTs,
          stopAtTs: args.stopTs ?? null,
          ...(player ? { isSeeking: true } : {}),
        });

        player?.seek({
          time: convertTsToTime(args.startTs!),
          ts: args.startTs!,
          playAfterSeek: args.playAfterSeek !== false,
        });
      },
      pause: ({ calledFromPlayer }: { calledFromPlayer?: boolean } = {}) => {
        debugLog('pause called (caller: %s)', calledFromPlayer ? 'player' : 'user');

        set({ isPlaying: false, isSeeking: false });

        if (!calledFromPlayer) {
          getPlayer()?.pause();
        }
      },
      onPlayerPlaying: () => {
        debugLog('player started playing');

        // Apply exclusive playback mode to all other players before resuming
        playbackRegistry.applyExclusivePlaybackMode(
          api as unknown as StoreApi<BasePlaybackState & BasePlaybackActions>,
        );

        set({ isPlaying: true, isSeeking: false });
      },
      setExclusivePlaybackMode: (mode: ExclusivePlaybackMode) => {
        debugLog('setExclusivePlaybackMode: mode=%s', mode);
        set({ exclusivePlaybackMode: mode });
      },
      mute: () => {
        debugLog('mute');
        getPlayer()?.mute();
        set({ isMuted: true });
      },
      unmute: () => {
        debugLog('unmute');
        getPlayer()?.unmute();
        set({ isMuted: false });
      },
      dispose: () => {
        debugLog('dispose');

        clearSubscriptions();
        getPlayer()?.dispose();
      },
    };
  };
};
